import { useEffect, useState } from "react";
import type { SettingsResponse } from "../../shared/types";
import { api } from "../lib/api";
import { type Colours, lookOf } from "./looks";
import type { LookMode } from "./parts";
import type { Run } from "./useSettingsData";

export interface BrandDraft {
  mode: LookMode;
  colours: Colours;
  fromName: string;
  footer: string;
}

const draftOf = (d: SettingsResponse): BrandDraft => ({
  mode: lookOf(d.brand),
  colours: { accent: d.brand.accent, brass: d.brand.brass, paper: d.brand.paper },
  fromName: d.brand.from_name,
  footer: d.brand.footer_text,
});

/** The brand as being changed: the colours and the email identity, drawn live and saved together. */
export function useBrandDraft(data: SettingsResponse, run: Run) {
  const saved = draftOf(data);
  const [draft, setDraft] = useState<BrandDraft>(saved);
  const [saving, setSaving] = useState(false);
  const savedKey = JSON.stringify({ ...saved, mode: undefined });
  // A save, or a change from setup, brings the draft back to what is stored.
  // Why only savedKey: it stands for the stored brand, and a new settings object with the same brand keeps the draft.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setDraft(draftOf(data)), [savedKey]);
  const dirty =
    draft.colours.accent !== saved.colours.accent ||
    draft.colours.brass !== saved.colours.brass ||
    draft.colours.paper !== saved.colours.paper ||
    draft.fromName !== saved.fromName ||
    draft.footer !== saved.footer;
  const save = async () => {
    setSaving(true);
    await run(() => api.settings.updateBrand({ ...draft.colours, from_name: draft.fromName.trim(), footer_text: draft.footer }), "Saved");
    setSaving(false);
  };
  return {
    draft,
    dirty,
    saving,
    save,
    discard: () => setDraft(draftOf(data)),
    pick: (mode: LookMode, colours: Colours) => setDraft((d) => ({ ...d, mode, colours })),
    setFromName: (fromName: string) => setDraft((d) => ({ ...d, fromName })),
    setFooter: (footer: string) => setDraft((d) => ({ ...d, footer })),
  };
}
